import DeleteIcon from "@mui/icons-material/DeleteOutlineRounded";
import ExportIcon from "@mui/icons-material/OutputRounded";
import { Button } from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import ActionsMenuButton from "@entrilia/shared/components/ActionsMenuButton";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { logError } from "@entrilia/shared/logging";
import { downloadFileFromUrl } from "@entrilia/shared/services/downloadFile";
import { stringToBase64 } from "@entrilia/shared/utilities/stringHelper";
import adminApi from "../../../../api/adminApi";
import type { UpdateUserConsentRequest, UserConsentDetails } from "../../../../api/types/userConsentTypes";
import useClientContext from "../../../../context/useClientContext";
import DeleteUserConsentDialog from "./DeleteUserConsentDialog";
import UpdateConsentVersionDialog from "./UpdateConsentVersionDialog";
import type { UserConsentEditState } from "./userConsentEditState";

interface Props {
  consentName: string;
  editState: UserConsentEditState;
  onCancel: () => void;
  onSaved: (updatedConsentDetails: UserConsentDetails) => void;
  onDeleted: () => void;
  actionsDisabled: boolean;
}

const updateUserConsent = withErrorHandling(adminApi.updateUserConsent);
const exportUserConsentAgreementsToExcel = withErrorHandling(adminApi.exportUserConsentAgreementsToExcel);

const UserConsentDetailsPageActions = ({
  consentName,
  editState,
  onCancel,
  onSaved,
  onDeleted,
  actionsDisabled,
}: Props) => {
  const { hasAnyPermission, hasPermissions } = useClientContext();
  const { sendNotificationError } = useNotificationContext();

  const [isExporting, setIsExporting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showUpdateVersionDialog, setShowUpdateVersionDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const hasEditPermissions = hasPermissions(["ManageInvestorPortalSettings"]);
  const hasPermissionsToExportUsers = hasAnyPermission(["ViewInvestorPortalUsers", "ManageInvestorPortalUsers"]);

  const handleExportConsentReport = async () => {
    setIsExporting(true);
    const [resp, error] = await exportUserConsentAgreementsToExcel(consentName);
    setIsExporting(false);

    if (error) {
      sendNotificationError(error.message);
      logError(error, "[UserConsentDetailsPageActions] exportUserConsentAgreementsToExcel");
      return;
    }

    await downloadFileFromUrl(resp.downloadUrl);
  };

  const handleConfirmSave = async (updateVersion: boolean) => {
    setShowUpdateVersionDialog(false);

    const payload: UpdateUserConsentRequest = {
      contentEncoded: stringToBase64(editState.htmlContent),
      activeFrom:
        editState.isActive && editState.activationDate !== null ? editState.activationDate.toISOString() : undefined,
      customAgreementText: editState.confirmationText,
      contentSource: editState.contentSource,
    };

    setIsSaving(true);

    const [updatedConsent, error] = await updateUserConsent(consentName, payload, updateVersion);

    setIsSaving(false);

    if (error) {
      logError(error, "[UserConsentEditor] updateUserConsent");
      sendNotificationError(error.message);
      return;
    }

    onSaved(updatedConsent);
  };

  const handleSave = async () => {
    if (editState.isActive) {
      setShowUpdateVersionDialog(true);
    } else {
      await handleConfirmSave(false);
    }
  };

  const isFormDirty = editState.touchedFields.length > 0;

  return (
    <>
      {hasPermissionsToExportUsers && (
        <Button
          loading={isExporting}
          disabled={actionsDisabled}
          onClick={handleExportConsentReport}
          variant="outlined"
          color="secondary"
          startIcon={<ExportIcon />}
        >
          Export Consent Report
        </Button>
      )}

      {hasEditPermissions && (
        <Button
          variant="text"
          color="secondary"
          disabled={actionsDisabled || !isFormDirty || editState.isEditingContent}
          onClick={onCancel}
        >
          Cancel
        </Button>
      )}

      {hasEditPermissions && (
        <Button
          variant="contained"
          disabled={
            actionsDisabled || !isFormDirty || !editState.validationResult.isValid || editState.isEditingContent
          }
          loading={isSaving}
          onClick={handleSave}
        >
          Save
        </Button>
      )}

      {hasEditPermissions && (
        <ActionsMenuButton
          disabled={actionsDisabled}
          items={[
            {
              icon: <DeleteIcon color="error" />,
              label: "Delete",
              onClick: () => setShowDeleteDialog(true),
            },
          ]}
        />
      )}

      <UpdateConsentVersionDialog
        open={showUpdateVersionDialog}
        onClose={() => setShowUpdateVersionDialog(false)}
        onConfirm={handleConfirmSave}
      />

      <DeleteUserConsentDialog
        open={showDeleteDialog}
        consentName={consentName}
        onClose={() => setShowDeleteDialog(false)}
        onDeleted={onDeleted}
      />
    </>
  );
};

export default UserConsentDetailsPageActions;
